<script setup>
import { WdsButtonLink, WdsPillActive, WdsPillSelect, WdsPropertyCard } from '@wds/components';
import { findPropertyTypeByKey } from '@/utils/propertyTypes.js';

const props = defineProps({
  propertyTypeObj: {
    type: Object,
    required: true,
  },
  locationName: {
    type: String,
    required: true,
  },
  locationList: {
    type: Array,
    required: true,
  },
});

// EVENTS
const emit = defineEmits(['sustainabilityPinClick']);

// COMPOSABLES
const { t } = useI18n();
const { useUrl } = useUrls();
const { useImage } = useImages();
const { useFormatter } = useFormatters();
const { getSustainabilityLevel } = useSustainability();
const activeLocation = ref(props.locationList[0]);

const hasLocationList = computed(() => {
  return props.locationList?.length > 0;
});

const popularInLocationText = computed(() => {
  return t('t_POPULARINLOCATION', { LOCATION: `<span>${props.locationName}</span>` });
});

const topRatedPropertiesInLocationText = computed(() => {
  return t('t_TOPRATEDPROPERTIESINLOCATION', {
    PROPERTYTYPE: t(props.propertyTypeObj?.transcode).toLowerCase(),
    LOCATION: `<strong>${fullActiveLocationName()}</strong>`,
  });
});

const linkToActiveCityPage = computed(() => {
  return useUrl.getCityPageUrl({
    propertyTypeSlug: props.propertyTypeObj?.slug,
    urlFriendlyContinent: activeLocation.value?.urlFriendlyContinent,
    urlFriendlyCountry: activeLocation.value?.urlFriendlyCountry,
    urlFriendlyCity: activeLocation.value?.urlFriendlyName,
  });
});

const viewAllPropertiesInLocationText = computed(() => {
  return t('t_VIEWALLPROPERTIESINLOCATION', {
    PROPERTYTYPE: t(props.propertyTypeObj?.transcode).toLowerCase(),
    LOCATION: activeLocation.value?.name,
  });
});

// METHODS
function setPillComponent(location) {
  return location.id === activeLocation.value?.id ? WdsPillActive : WdsPillSelect;
}
function setActiveLocation(location) {
  activeLocation.value = location;
}

function getPopularPropertiesFromLocation(location) {
  return location.properties.slice(0, 4);
}

function fullActiveLocationName() {
  const locationNames = [];

  if (activeLocation.value?.name) {
    locationNames.push(activeLocation.value?.name);
  }

  if (activeLocation.value?.country) {
    locationNames.push(activeLocation.value?.country);
  }
  return locationNames.join(', ');
}
function getPropertyDetailsForCard(property) {
  const propertyType = findPropertyTypeByKey(property?.type);
  const propertyImage = property?.image?.medium || null;
  const hasPrivateRooms = property.privateMinPrice.value > 0;
  const hasDormRooms = property.sharedMinPrice.value > 0;
  const propSlides = propertyImage ? [useImage.getImage(propertyImage)] : [];
  const hrefLink = useUrl.getPropertyPageUrl({
    propertyTypeSlug: t(propertyType.slug),
    propertyId: property.id,
    urlFriendlyProperty: property.urlFriendlyName,
  });

  const propertySustainability = getSustainabilityLevel(property.sustainability);

  return {
    type: t(propertyType?.transcode_s),
    typeTheme: propertyType?.theme,
    name: property.name,
    href: hrefLink,
    gallery: {
      slides: propSlides,
      alt: property.name,
      title: property.name,
    },
    rating: {
      score: property.avgRating,
      totalReviews: property?.numberReviews,
    },
    distance: property?.cityCenterDistance
      ? t('t_FROMCITYCENTER', { DISTANCE: property.cityCenterDistance.toString() })
      : null,
    minPrivatePrice: {
      curPrice: hasPrivateRooms
        ? useFormatter.formatCurrency(property.privateMinPrice.value, property.privateMinPrice.currency)
        : null,
    },
    minDormPrice: {
      curPrice: hasDormRooms
        ? useFormatter.formatCurrency(property.sharedMinPrice.value, property.sharedMinPrice.currency)
        : null,
    },
    translations: {
      tPrivatesFrom: t('t_PRIVATESFROM'),
      tNoPrivatesAvailable: t('t_NOPRIVATESAVAILABLE'),
      tDormsFrom: t('t_DORMSFROM'),
      tNoDormsAvailable: t('t_NODORMSAVAILABLE'),
      tNoAvailability: t('t_NOAVAILABILITY'),
    },
    sustainabilityLevel: null,
    sustainabilityBadge: Boolean(propertySustainability),
  };
}

function handleSustainabilityPinClick(propertySustainabilityInfo) {
  emit('sustainabilityPinClick', getSustainabilityLevel(propertySustainabilityInfo));
}
</script>

<template>
  <section
    v-if="hasLocationList"
    class="popular-properties-in-location-section"
  >
    <h2
      class="popular-properties-heading"
      v-html="popularInLocationText"
    />

    <div class="popular-properties-locations-wrapper">
      <div class="popular-properties-locations">
        <component
          :is="setPillComponent(location)"
          v-for="location in locationList"
          :key="location.id"
          :text="location.name"
          class="popular-properties-location"
          @click.prevent="setActiveLocation(location)"
        />
      </div>
    </div>
    <div
      class="popular-properties-top-rated"
      v-html="topRatedPropertiesInLocationText"
    />
    <div class="popular-properties-list-wrapper">
      <div
        v-for="location in locationList"
        v-show="location.id === activeLocation.id"
        :key="`props${location.id}`"
        class="popular-properties-card-list"
      >
        <WdsPropertyCard
          v-for="property in getPopularPropertiesFromLocation(location)"
          :key="property.id"
          v-bind="getPropertyDetailsForCard(property)"
          @sustainability-pin-click="handleSustainabilityPinClick(property.sustainability)"
        />
      </div>
    </div>
    <WdsButtonLink
      :href="linkToActiveCityPage"
      :text="viewAllPropertiesInLocationText"
      :title="viewAllPropertiesInLocationText"
      :aria-label="viewAllPropertiesInLocationText"
      rel="noopener"
      icon-end="chevron-right"
      class="popular-properties-view-all"
    />
  </section>
</template>

<style lang="scss" scoped>
.popular-properties-in-location-section {
  display: flex;
  flex-direction: column;

  .popular-properties-heading {
    @include title-2-bld;

    color: $wds-color-ink-darker;
    margin-bottom: $wds-spacing-m;
    padding: 0 $wds-spacing-m;

    :deep(span) {
      color: $wds-color-purple;
    }
  }

  .popular-properties-locations-wrapper {
    display: flex;
    justify-content: center;
    overflow: hidden;
    border-radius: $wds-spacing-l;
    width: 100%;
    margin-bottom: $wds-spacing-m;

    .popular-properties-locations {
      display: flex;
      gap: $wds-spacing-s;
      overflow-x: auto;
      padding: $wds-spacing-m;

      .popular-properties-location {
        white-space: nowrap;
      }
    }
  }

  .popular-properties-top-rated {
    margin-bottom: $wds-spacing-m;
    padding: 0 $wds-spacing-m;

    &::first-letter {
      text-transform: uppercase;
    }
  }

  .popular-properties-list-wrapper {
    overflow: hidden;
    border-radius: $wds-spacing-l;
    width: 100%;

    .popular-properties-card-list {
      display: inline-flex;
      gap: $wds-spacing-m;
      overflow-x: auto;
      padding: $wds-spacing-m;
      width: 100%;

      > * {
        flex: 0 0 wds-rem(320px);
        height: auto;
      }
    }
  }

  .popular-properties-view-all {
    padding: 0 $wds-spacing-m;
    cursor: pointer;
    align-self: flex-end;
  }

  @include tablet {
    .popular-properties-heading,
    .popular-properties-locations-wrapper .popular-properties-locations,
    .popular-properties-top-rated,
    .popular-properties-list-wrapper .popular-properties-card-list {
      padding-inline: $wds-spacing-l;
    }
  }

  @include desktop {
    align-items: center;

    .popular-properties-heading {
      @include title-1-bld;

      margin-bottom: $wds-spacing-l;
      padding-inline: wds-rem(40px);
    }

    .popular-properties-locations-wrapper {
      margin-bottom: $wds-spacing-m;

      .popular-properties-locations {
        padding-inline: wds-rem(40px);
      }
    }

    .popular-properties-top-rated {
      margin-bottom: $wds-spacing-xl;
      padding-inline: wds-rem(40px);
    }

    .popular-properties-list-wrapper .popular-properties-card-list {
      justify-content: flex-start;
      overflow-x: auto;
      padding-inline: $wds-spacing-xs;
      margin: 0;
      width: 100%;

      > * {
        flex-grow: 1;
        min-width: wds-rem(320px);
        max-width: 25%;
      }
    }
  }
}
</style>
